/**
 * WalletConnect v2 wrapper.
 *
 * - Lazy-loads @walletconnect/ethereum-provider on first use so users with
 *   working injected wallets (desktop MetaMask, Android Trust, etc.) never
 *   pay the extra bundle weight.
 * - Singleton: the EthereumProvider instance is reused across the app so
 *   active sessions persist between pages.
 * - Exposes an EIP-1193-compliant provider so the rest of the codebase can
 *   call `provider.request(...)`, `provider.on(...)` exactly like an
 *   injected window.ethereum.
 *
 * @see https://docs.reown.com/advanced/multichain/multichain-walletconnect-v2
 */

import { uistep, uiwarn, uierror } from '@/utils/uiLogger'

const PROJECT_ID = '29916874a59958e9b395225b4e6af6de'

const APP_METADATA = {
  name: 'UBS DApp',
  description: 'UBS DApp Trading Platform',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://www.bitmmarkets.com',
  icons: typeof window !== 'undefined' ? [window.location.origin + '/favicon.ico'] : []
}

// Ethereum mainnet only (chainId=1) per current product requirement.
// Add more later by extending optionalChains; the API stays the same.
const OPTIONAL_CHAINS = [1]

let initPromise = null
let providerInstance = null
let connectInFlight = null

async function lazyImportWcModule() {
  // webpackChunkName isolates WC into its own chunk so the main bundle stays small.
  return await import(/* webpackChunkName: "walletconnect" */ '@walletconnect/ethereum-provider')
}

/**
 * Initialize (but do NOT connect) the WC provider. Idempotent — repeated calls
 * return the same instance.
 */
export async function initWalletConnectProvider() {
  if (providerInstance) return providerInstance
  if (initPromise) return initPromise

  initPromise = (async () => {
    uistep('WC', 'init() loading @walletconnect/ethereum-provider...')
    const t0 = Date.now()
    const mod = await lazyImportWcModule()
    const EthereumProvider = mod.EthereumProvider || (mod.default && mod.default.EthereumProvider)
    if (!EthereumProvider) {
      throw new Error('EthereumProvider export not found in @walletconnect/ethereum-provider')
    }
    uistep('WC', 'module loaded in', (Date.now() - t0) + 'ms; calling EthereumProvider.init()...')
    const t1 = Date.now()
    const provider = await EthereumProvider.init({
      projectId: PROJECT_ID,
      metadata: APP_METADATA,
      showQrModal: true,
      optionalChains: OPTIONAL_CHAINS,
      // Force the WC modal to handle the current ethereum mainnet flow.
      // We keep this list short so the modal doesn't ask users for unrelated chains.
      methods: [
        'eth_sendTransaction',
        'eth_signTransaction',
        'personal_sign',
        'eth_sign',
        'eth_signTypedData',
        'eth_signTypedData_v4',
        'wallet_switchEthereumChain',
        'wallet_addEthereumChain'
      ],
      events: ['chainChanged', 'accountsChanged']
    })
    uistep('WC', 'init() completed in', (Date.now() - t1) + 'ms', 'session=', !!provider.session)
    providerInstance = provider
    return provider
  })().catch((err) => {
    initPromise = null
    uierror('[WC] init failed:', err && err.message ? err.message : err)
    throw err
  })

  return initPromise
}

/**
 * Open the WC modal (or use existing session) and resolve when the user
 * has connected. Returns the EIP-1193 provider.
 */
export async function connectViaWalletConnect() {
  const provider = await initWalletConnectProvider()

  if (provider.session && Array.isArray(provider.accounts) && provider.accounts.length > 0) {
    uistep('WC', 'reusing existing session, accounts=', provider.accounts.length)
    return provider
  }

  if (connectInFlight) {
    uistep('WC', 'connect already in flight, awaiting existing promise')
    return connectInFlight
  }

  connectInFlight = (async () => {
    uistep('WC', 'opening connect modal...')
    try {
      // EthereumProvider.connect() opens the QR/wallet picker modal and resolves
      // once the wallet has paired. It throws if the user closes the modal.
      await provider.connect()
      uistep('WC', 'connected, accounts=', (provider.accounts || []).length, 'chainId=', provider.chainId)
      return provider
    } catch (e) {
      uiwarn('[WC] connect rejected/cancelled:', e && e.message ? e.message : e)
      throw e
    }
  })().finally(() => {
    connectInFlight = null
  })

  return connectInFlight
}

/**
 * Returns the connected WC provider if and only if a session is currently
 * active. Used by the synchronous resolver to short-circuit when WC is ready.
 */
export function getActiveWalletConnectProvider() {
  if (providerInstance && providerInstance.session && Array.isArray(providerInstance.accounts) && providerInstance.accounts.length > 0) {
    return providerInstance
  }
  return null
}

/**
 * Disconnects the current WC session (and clears local cache). Safe to call
 * even when no session exists.
 */
export async function disconnectWalletConnect() {
  if (!providerInstance) return
  try {
    if (providerInstance.session) {
      await providerInstance.disconnect()
      uistep('WC', 'disconnected')
    }
  } catch (e) {
    uiwarn('[WC] disconnect error:', e && e.message ? e.message : e)
  } finally {
    providerInstance = null
    initPromise = null
    connectInFlight = null
  }
}
